import P from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Fragment, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Box, CenteredContainer } from '@nerdwallet/currency';
import classNames from 'classnames';
import { compose } from 'redux';

import styles from './grid.module.less';
import Row from '../row/row';
import UniversalText from '../universal-text/universal-text';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import { updateComponent } from '../../containers/builder/actions';
import { isInBuilder } from '../../lib/helpers';

import GridItems from './grid-items';

import editingConfig from './lib/editing-config';

const defaultProps = {
  gridItems: [
    {
      $unique_key: new Date().getTime(),
    },
  ],
};

const Grid = ({
  container,
  title,
  subtitle,
  centeredInParentBackground,
  gridItems,
  columns,
  gap,
  centeredInParent,
  disableDefaultProps,
  updateComponentProps,
  untouched,
  selectedComponent,
  componentId,
  floatingGrid,
  floatTop,
  floatLeft,
  floatRight,
  floatBottom,
  floatIndex,
  history,
}) => {
  useEffect(() => {
    if (untouched && selectedComponent?.id === componentId) {
      updateComponentProps({
        ...selectedComponent,
        props: { ...defaultProps, untouched: false },
      });
    }
  }, [componentId, selectedComponent, untouched, updateComponentProps]);

  const isBuilderPage = isInBuilder(history?.location?.pathname);
  const outputFloatingGridTransparency =
    floatingGrid && !disableDefaultProps && isBuilderPage;
  const isGridInEditMode = selectedComponent?.id === componentId;
  const outputDashedBorder =
    !disableDefaultProps && isBuilderPage && isGridInEditMode;
  const outputGridItemIndicator =
    !disableDefaultProps && isBuilderPage && isGridInEditMode;

  const floatingGridStyle = floatingGrid
    ? {
        position: 'absolute',
        // We calling .replace() for old pages backwards compatibility
        top: `${floatTop?.replace('px', '')}px` || 0,
        bottom: `${floatBottom?.replace('px', '')}px` || 0,
        left: `${floatLeft?.replace('px', '')}px` || 0,
        right: `${floatRight?.replace('px', '')}px` || 0,
        zIndex: floatIndex,
      }
    : null;

  const content = (
    <Fragment>
      {title && <UniversalText {...title} data-testid="grid-title" />}
      {subtitle && (
        <UniversalText
          {...subtitle}
          defaultOptions={{ text: 'text' }}
          data-testid="grid-subtitle"
        />
      )}

      <div>
        <Row
          columns={columns}
          style={{ ...(gap && { gap: `${gap}px` }) }}
          className={classNames([
            {
              [styles.itemComponentTransparency]:
                outputFloatingGridTransparency,
            },
          ])}
        >
          <GridItems
            outputGridItemIndicator={outputGridItemIndicator}
            outputDashedBorder={outputDashedBorder}
            isBuilderPage={isBuilderPage}
            items={gridItems}
            disableDefaultProps={disableDefaultProps}
          />
        </Row>
      </div>
    </Fragment>
  );

  return (
    <Box position="relative">
      <UniversalContainer
        {...container}
        style={{ ...(floatingGrid && floatingGridStyle) }}
        data-testid="grid-root-container"
      >
        {centeredInParent ? (
          <CenteredContainer>
            <UniversalContainer {...centeredInParentBackground}>
              {content}
            </UniversalContainer>
          </CenteredContainer>
        ) : (
          content
        )}
      </UniversalContainer>
    </Box>
  );
};

Grid.displayName = 'Grid';

Grid.propTypes = {
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  centeredInParentBackground: P.object,
  columns: P.string,
  gridItems: P.array,
  gap: P.array,
  centeredInParent: P.bool,
  disableDefaultProps: P.bool,
  untouched: P.bool,
  selectedComponent: P.object,
  componentId: P.string,
  flatOutline: P.object,
  updateComponentProps: P.func,
  floatingGrid: P.bool,
  floatTop: P.string,
  floatLeft: P.string,
  floatRight: P.string,
  floatBottom: P.string,
  floatIndex: P.number,
  history: P.object,
};

Grid.defaultProps = {
  container: {},
  centeredInParentBackground: {},
  columns: '1',
  gridItems: [],
  gap: '0',
  centeredInParent: false,
  disableDefaultProps: false,
  floatingGrid: false,
  floatTop: '0',
  floatLeft: '0',
  floatRight: '0',
  floatBottom: '0',
  floatIndex: 1,
};

Grid.getEditingConfig = editingConfig;

const mapDispatchToProps = (dispatch) => ({
  updateComponentProps: (componentData) => {
    dispatch(updateComponent(componentData));
  },
});

export default compose(
  withRouter,
  connect(
    createStructuredSelector({
      size: (state) => state?.mediaQuery,
      selectedComponent: ({ builder }) =>
        builder?.flatOutline?.[builder?.selected], // get Editor Panel current component data
    }),
    mapDispatchToProps
  )
)(Grid);
